import React, { useMemo, useState } from 'react';
import {
  Check,
  Bot,
  Workflow,
  Database,
  Lightbulb,
  Zap,
  Clock,
  Calendar,
  Compass,
  ArrowRight,
  Send,
  Mail,
  GraduationCap,
  Target,
  Brain,
} from 'lucide-react';
import {
  brand,
  expectations,
  serviceOptions,
  timelineOptions,
  budgetOptions,
} from '../mock';
import { toast } from 'sonner';
import { submitLead } from '../lib/supabase';

const iconMap = { Bot, Workflow, Database, Lightbulb, Zap, Clock, Calendar, Compass, GraduationCap, Target, Brain };

const ContactSection = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    website: '',
    services: [],
    timeline: '',
    challenge: '',
    budget: '',
  });

  const [submitting, setSubmitting] = useState(false);

  const progress = useMemo(() => {
    let filled = 0;
    if (form.name) filled++;
    if (form.email) filled++;
    if (form.services.length) filled++;
    if (form.timeline) filled++;
    if (form.challenge) filled++;
    if (form.budget) filled++;
    return filled;
  }, [form]);

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(id)
        ? f.services.filter((s) => s !== id)
        : [...f.services, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (progress < 6) {
      toast('Please complete all required fields', { description: `${progress}/6 filled` });
      return;
    }
    setSubmitting(true);
    const result = await submitLead(form);
    setSubmitting(false);

    if (!result.ok) {
      toast.error('Something went wrong', { description: result.error || 'Please try again or email us directly.' });
      return;
    }

    toast.success('Consultation request sent!', {
      description: 'Redirecting you to book a time\u2026',
    });
    setForm({ name: '', email: '', website: '', services: [], timeline: '', challenge: '', budget: '' });

    // Redirect to Cal.com after a short delay so the user sees confirmation
    setTimeout(() => {
      window.location.href = brand.calendarUrl;
    }, 1200);
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#F6EFE4]">
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
      <div className="relative mx-auto max-w-6xl px-5">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-[#C2603A]">
            <span className="w-5 h-px bg-[#C2603A]/60" /> Get Started <span className="w-5 h-px bg-[#C2603A]/60" />
          </div>
          <h2 className="serif-display text-4xl md:text-6xl mt-4 text-ink">
            Let's Talk About Your Project
          </h2>
          <p className="mt-4 text-muted max-w-2xl mx-auto">
            Tell us what you're building and we'll show you how AI can accelerate it.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
          {/* Left panel */}
          <aside className="lg:col-span-2 space-y-5">
            <div className="rounded-2xl bg-white border border-ink-10 p-6 soft-card">
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink font-medium">Form Progress</span>
                <span className="text-[#C2603A] font-semibold">{progress}/6</span>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-ink/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#E08C5F] to-[#C2603A] rounded-full transition-all duration-500"
                  style={{ width: `${(progress / 6) * 100}%` }}
                />
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-ink-10 p-6 soft-card">
              <h3 className="serif-display text-2xl">What to expect</h3>
              <ul className="mt-4 space-y-3">
                {expectations.map((e) => (
                  <li key={e} className="flex items-center gap-3 text-[14.5px] text-ink/85">
                    <span className="w-5 h-5 rounded-full bg-[#FCE7D6] text-[#C2603A] flex items-center justify-center">
                      <Check className="w-3 h-3" />
                    </span>
                    {e}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-white border border-ink-10 p-6 soft-card">
              <div className="text-sm text-muted">Prefer to email directly?</div>
              <a href={`mailto:${brand.email}`} className="mt-1 inline-flex items-center gap-2 text-[#C2603A] font-medium hover:underline">
                <Mail className="w-4 h-4" /> {brand.email} <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>

            <div className="rounded-2xl bg-white border border-ink-10 p-6 soft-card">
              <div className="text-sm text-muted">Skip the form?</div>
              <a
                href={brand.calendarUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-2 text-[#C2603A] font-medium hover:underline"
              >
                <Calendar className="w-4 h-4" /> Book a 30-min call <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </aside>

          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-3 rounded-2xl bg-white border border-ink-10 p-6 md:p-8 soft-card space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-ink">Name <span className="text-[#C2603A]">*</span></label>
                <input
                  type="text"
                  placeholder="Your full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-ink-10 bg-white px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#C2603A]/40 focus:border-[#C2603A]/40 transition"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-ink">Email <span className="text-[#C2603A]">*</span></label>
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-ink-10 bg-white px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#C2603A]/40 focus:border-[#C2603A]/40 transition"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-ink">Website URL <span className="text-muted">(optional)</span></label>
              <input
                type="url"
                placeholder="https://yourcompany.com"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                className="mt-2 w-full rounded-xl border border-ink-10 bg-white px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#C2603A]/40 focus:border-[#C2603A]/40 transition"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-ink">Services of Interest <span className="text-[#C2603A]">*</span></label>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {serviceOptions.map((s) => {
                  const Icon = iconMap[s.icon];
                  const active = form.services.includes(s.id);
                  return (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => toggleService(s.id)}
                      className={`chip ${active ? 'active' : ''} rounded-xl px-4 py-3 flex items-center gap-2 text-[14px] text-left`}
                    >
                      <Icon className={`w-4 h-4 ${active ? 'text-[#C2603A]' : 'text-muted'}`} />
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-ink">Project Timeline <span className="text-[#C2603A]">*</span></label>
              <div className="mt-3 flex flex-wrap gap-2">
                {timelineOptions.map((t) => {
                  const Icon = iconMap[t.icon];
                  const active = form.timeline === t.id;
                  return (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => setForm({ ...form, timeline: t.id })}
                      className={`chip ${active ? 'active' : ''} rounded-full px-4 py-2 flex items-center gap-2 text-[13.5px]`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-ink">Primary Business Challenge <span className="text-[#C2603A]">*</span></label>
              <textarea
                rows={4}
                placeholder="Tell us about the biggest challenge you'd like AI to solve..."
                value={form.challenge}
                onChange={(e) => setForm({ ...form, challenge: e.target.value })}
                className="mt-2 w-full rounded-xl border border-ink-10 bg-white px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#C2603A]/40 focus:border-[#C2603A]/40 transition resize-none"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-ink">Estimated Budget <span className="text-[#C2603A]">*</span></label>
              <div className="mt-3 flex flex-wrap gap-2">
                {budgetOptions.map((b) => {
                  const active = form.budget === b.id;
                  return (
                    <button
                      type="button"
                      key={b.id}
                      onClick={() => setForm({ ...form, budget: b.id })}
                      className={`chip ${active ? 'active' : ''} rounded-full px-4 py-2 text-[13.5px]`}
                    >
                      {b.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full rounded-xl py-3.5 text-[15px] font-medium flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {submitting ? 'Sending\u2026' : 'Book Your Free Consultation'}
              {!submitting && <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
